import { onMounted, ref } from 'vue'
import type {
  PaymentMethodResult,
  Stripe,
  StripeCardElement,
} from '@stripe/stripe-js'

import { createCardElement, getStripe } from '@/shared/services/stripe'
import {
  BillingQueryDocument,
  PlatformType,
  useCreateSubscriptionMutation,
  type BillingPlan,
  type BillingQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'

export function useResubscribe() {
  const billingStore = useBillingStore()
  const { mutate: createSubscription } = useCreateSubscriptionMutation()

  const stripe = ref<Stripe | null>(null)
  const card = ref<StripeCardElement | null>(null)
  const loading = ref(false)
  const errorMessage = ref('')

  onMounted(async () => {
    stripe.value = await getStripe()
    if (!stripe.value) return

    card.value = createCardElement(stripe.value)
    card.value.on('change', () => {
      errorMessage.value = ''
    })
    card.value.mount('#card')
  })

  const resubscribe = async (useNewCard: boolean) => {
    const productId = billingStore.billing?.plan.id
    if (!productId) return false

    loading.value = true
    errorMessage.value = ''

    try {
      let paymentMethodId: string | undefined
      let result: PaymentMethodResult | undefined

      if (useNewCard) {
        if (!stripe.value || !card.value) return false

        result = await stripe.value.createPaymentMethod({
          type: 'card',
          card: card.value,
        })

        if (result.error) {
          errorMessage.value = result.error.message || 'Something went wrong'
          loading.value = false
          return false
        }

        paymentMethodId = result.paymentMethod?.id
      }

      const response = await createSubscription(
        {
          productId,
          platform: PlatformType.Web,
          ...(paymentMethodId && { paymentMethodId }),
        },
        {
          update(cache, { data }) {
            cache.updateQuery<BillingQueryQuery>(
              { query: BillingQueryDocument },
              (current) => {
                if (!current || !data?.createSubscription.plan) return current

                const cardData = result?.paymentMethod?.card
                return {
                  ...current,
                  billing: {
                    ...current.billing,
                    plan: data.createSubscription.plan as BillingPlan,
                    billingAlert: null,
                    ...(cardData && {
                      paymentMethod: {
                        __typename: 'PaymentMethod' as const,
                        card: {
                          __typename: 'Card' as const,
                          last4: cardData.last4,
                          type: cardData.brand,
                        },
                        requiresAction: null,
                      },
                    }),
                  },
                } as BillingQueryQuery
              }
            )
          },
        }
      )

      const subscription = response?.data?.createSubscription

      if (subscription?.failedMessage) {
        errorMessage.value = subscription.failedMessage
        return false
      }

      if (subscription?.requiresAction?.clientSecret) {
        const stripeInstance = stripe.value || (await getStripe())

        if (!stripeInstance) {
          errorMessage.value =
            'We could not confirm your payment. Please try again.'
          return false
        }

        const { error } = await stripeInstance.confirmCardPayment(
          subscription.requiresAction.clientSecret
        )

        if (error) {
          errorMessage.value = error.message || 'Payment authentication failed.'
          return false
        }
      }

      return true
    } catch (error) {
      errorMessage.value =
        error instanceof Error ? error.message : 'Something went wrong'
      return false
    } finally {
      loading.value = false
    }
  }

  return { loading, errorMessage, resubscribe }
}
